import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, ExternalLink, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useTranslation } from '../i18n';

export default function PaymentSettingsTab() {
  const { t } = useTranslation();
  // Check Stripe Connect status
  const { data: connectStatus, isLoading } = useQuery({
    queryKey: ['stripe-connect-status'],
    queryFn: () => api.get('/payments/connect/status').then(r => r.data),
    retry: false,
  });

  // Check Stripe config
  const { data: stripeConfig } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: () => api.get('/payments/config').then(r => r.data),
    retry: false,
  });

  // Start Connect onboarding
  const onboardMutation = useMutation({
    mutationFn: () => api.post('/payments/connect/onboard'),
    onSuccess: (res) => {
      const url = res.data?.onboarding_url || res.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Failed to generate onboarding link');
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to start Stripe onboarding');
    }
  });

  const isConnected = connectStatus?.charges_enabled || connectStatus?.connected;
  const isPending = connectStatus?.details_submitted && !isConnected;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stripe Connect Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Processing
          </CardTitle>
          <CardDescription>
            Connect your Stripe account to receive payments directly from buyers.
            PocketFiler never holds your funds — payments go straight to your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isConnected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <div>
                  <p className="font-medium text-green-700 dark:text-green-400">Stripe Connected</p>
                  <p className="text-sm text-muted-foreground">
                    You can receive payments from buyers through PocketFiler.
                  </p>
                </div>
              </div>
              {connectStatus?.account_id && (
                <p className="text-xs text-muted-foreground">
                  Account: {connectStatus.account_id}
                </p>
              )}
              <Button variant="outline" size="sm" asChild>
                <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open Stripe Dashboard
                </a>
              </Button>
            </div>
          ) : isPending ? (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your Stripe account is pending verification. This usually takes 1-2 business days.
                  You'll be able to receive payments once Stripe completes their review.
                </AlertDescription>
              </Alert>
              <Button
                variant="outline"
                onClick={() => onboardMutation.mutate()}
                disabled={onboardMutation.isLoading}
              >
                {onboardMutation.isLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...</>
                ) : (
                  'Complete Stripe Setup'
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                To receive payments through PocketFiler, connect your Stripe account.
                This is a one-time setup that takes about 5 minutes.
              </p>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2 text-sm">
                <p className="font-medium">What happens when you connect:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Buyers can pay invoices directly through PocketFiler</li>
                  <li>• Payments go straight to your bank account via Stripe</li>
                  <li>• PocketFiler takes a small platform fee (2.5%)</li>
                  <li>• We never hold or touch your funds</li>
                </ul>
              </div>
              <Button
                onClick={() => onboardMutation.mutate()}
                disabled={onboardMutation.isLoading}
                className="w-full sm:w-auto"
              >
                {onboardMutation.isLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Setting up...</>
                ) : (
                  <><CreditCard className="h-4 w-4 mr-2" /> Connect Stripe Account</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Platform Fee Info */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Fees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">PocketFiler Platform Fee</span>
              <Badge variant="outline">{stripeConfig?.platform_fee_percent || 2.5}%</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Stripe Processing Fee</span>
              <Badge variant="outline">2.9% + $0.30</Badge>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              Platform fees are automatically deducted from each transaction.
              Stripe processing fees are standard and charged by Stripe directly.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
